<template>
  <vl-region>
    <vl-layout>
      <PbsGoBackLink />
      <template v-if="!loading">
        <vl-grid>
          <vl-column width="8" width-xs="12">
            <vl-title tag-name="h1"> {{ $t('organisations.detail.title') }} </vl-title>
          </vl-column>
          <vl-column width="4" width-xs="12">
            <vl-action-group
              v-if="detail && !editStore.isEdit(OrganisationEditType.Organisation, detail.id)"
              mod-align-right
              mod-space
              between
            >
              <vl-button
                icon="pencil"
                mod-icon-before
                mod-secondary
                :mod-disabled="editStore.editing"
                @click="editStore.openEdit(OrganisationEditType.Organisation, detail.id)"
              >
                {{ $t('general.edit') }}
              </vl-button>
              <vl-button icon="trash" mod-icon-before mod-error @click="showDeleteModal">{{
                $t('general.delete')
              }}</vl-button>
            </vl-action-group>
            <vl-action-group v-else mod-align-right mod-space between>
              <vl-button icon="save" :mod-loading="reloading" mod-icon-before @click="save">{{
                $t('general.save')
              }}</vl-button>
              <vl-button
                icon="cross"
                :mod-loading="loading"
                mod-icon-before
                mod-secondary
                mod-error
                @click="editStore.closeEdit"
              >
                {{ $t('general.cancel') }}
              </vl-button>
            </vl-action-group>
          </vl-column>
        </vl-grid>
        <vl-grid
          v-if="detail"
          :class="{
            edit: editStore.isEdit(OrganisationEditType.Organisation, detail.id),
            'vl-u-spacer-bottom--medium': true
          }"
        >
          <vl-column>
            <OrganisationEdit v-if="editStore.isEdit(OrganisationEditType.Organisation, detail.id)" ref="editForm" />
            <OrganisationRead v-else :detail="detail" />
          </vl-column>
        </vl-grid>
      </template>
      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('organisations.detail.loading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>
<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute, onBeforeRouteLeave, useRouter } from 'vue-router';
import { useOrganisationDetailStore } from '../../store/organisation/organisation-detail.store';
import { useOrganisationEditStore, OrganisationEditType } from '../../store/organisation/organisation-edit.store';
import { OrganisationDetailDto } from '@/api/backoffice/clients';
import OrganisationRead from './OrganisationRead.vue';
import OrganisationEdit from './OrganisationEdit.vue';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useI18n } from 'vue-i18n';

const store = useOrganisationDetailStore();
const editStore = useOrganisationEditStore();
const modalStore = useModalStore();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const editForm = ref();

const detail = computed((): OrganisationDetailDto => {
  return store.detail;
});

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const id = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

onMounted(async () => {
  editStore.init();
  await store.get(id.value);
});

const save = () => {
  editForm.value.onSubmit();
};

const showDeleteModal = () => {
  modalStore.showDeleteModal({
    title: t('organisations.detail.delete.title'),
    props: {
      navigationRouteOnSuccess: { name: 'configuration', hash: '#organisation' },
      confirm: () => store.delete(id.value)
    }
  });
};

onBeforeRouteLeave((to, from, next) => {
  const id = parseInt(from.params.id as string, 10);
  if (id && store) {
    store.clear(id);
  }
  next();
});
</script>
<style lang="scss" scoped>
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colorsRaw.scss';
@import '@govflanders/vl-ui-design-system-style/scss/core/setting/_colors.scss';

.pbs-form-group {
  background: $vl-alt-bg;
  padding: 3rem;
}
</style>
